import React from 'react';
import { Box, Typography, IconButton, List, Chip, Alert, Badge, Skeleton, Tooltip } from '@mui/material';
import { Tune, AutoAwesome, Sort, CheckCircle, WorkOutlineOutlined, ManageSearch, Leaderboard } from '@mui/icons-material';
import MissionSearchBar from './MissionSearchBar';
import SearchFiltersDialog from './SearchFiltersDialog';
import CandidatCard from './CandidatCard';
import LancerRecherchePopover from './LancerRecherchePopover';
import RerankPopover from './RerankPopover';
import { AppButton } from '../../../components/UI';

// Guided empty state: shows where the user is in the 3-step flow.
const Steps = ({ current }) => {
    const steps = [
        { icon: WorkOutlineOutlined, title: 'Choisir une mission', text: 'Recherchez la mission active à pourvoir.' },
        { icon: ManageSearch, title: 'Lancer la recherche IA', text: 'Le mode hybride donne en général les meilleurs résultats.' },
        { icon: Leaderboard, title: 'Comparer les candidats', text: 'Classés par score, avec le détail par critère.' },
    ];
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 380, mx: 'auto', width: '100%' }}>
            {steps.map((s, i) => {
                const done = i < current;
                const active = i === current;
                const Icon = s.icon;
                return (
                    <Box key={s.title} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '12px',
                        border: `1.5px solid ${active ? '#03407b' : done ? '#86efac' : '#e2e8f0'}`,
                        background: active ? '#f0f7fd' : done ? '#f0fdf4' : '#fff',
                        opacity: i > current ? 0.6 : 1,
                    }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: done ? '#dcfce7' : active ? '#03407b' : '#f1f5f9', color: done ? '#16a34a' : active ? '#fff' : '#94a3b8',
                        }}>
                            {done ? <CheckCircle sx={{ fontSize: 20 }} /> : <Icon sx={{ fontSize: 20 }} />}
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>{i + 1}. {s.title}</Typography>
                            <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{s.text}</Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

const CandidatsPanel = ({ state }) => {
    const {
        filter, setFilter, limit, setLimit,
        candidats, rawCandidats, loading, rerankLoading,
        selectedCandidat, setSelectedCandidat,
        error, setError,
        shortlistLimit, setShortlistLimit,
        lancerAnchorEl, setLancerAnchorEl,
        rerankAnchorEl, setRerankAnchorEl,
        missionsLoading, missionsError,
        dropdownOpen, setDropdownOpen,
        searchText, setSearchText,
        filterAnchorEl, setFilterAnchorEl,
        jobUuid, filteredMissions,
        availableSkills, availableLanguages, availableEducation,
        availableSeniorities, minCandidateExp, maxCandidateExp,
        experienceMin, setExperienceMin, experienceMax, setExperienceMax,
        seniorityLevel, setSeniorityLevel, selectedSkills, setSelectedSkills,
        selectedLanguages, setSelectedLanguages, selectedEducation, setSelectedEducation,
        selectedLocation, setSelectedLocation,
        loadMissions, handleSelectMission, handleClearSearch,
        lancerLaRecherche, lancerLeReranking,
    } = state;

    const activeFilters = [
        experienceMin > 0 || experienceMax < 20,
        seniorityLevel !== 'Tous',
        selectedSkills.length > 0,
        selectedLanguages.length > 0,
        selectedEducation && selectedEducation !== 'Tous',
        selectedLocation && selectedLocation.trim() !== '',
    ].filter(Boolean).length;

    const busy = loading || rerankLoading;
    const step = !jobUuid ? 0 : rawCandidats.length === 0 ? 1 : 2;

    return (
        <Box sx={{ flex: 1, p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0 }}>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', position: 'relative' }}>
                <MissionSearchBar
                    searchText={searchText} setSearchText={setSearchText}
                    dropdownOpen={dropdownOpen} setDropdownOpen={setDropdownOpen}
                    filteredMissions={filteredMissions} missionsLoading={missionsLoading}
                    missionsError={missionsError} jobUuid={jobUuid}
                    onSelectMission={handleSelectMission} onLoadMissions={loadMissions}
                    onClear={handleClearSearch}
                />
                <Tooltip title={rawCandidats.length ? 'Filtrer les résultats' : 'Lancez une recherche pour pouvoir filtrer'}>
                    <span>
                        <IconButton
                            aria-label="Ouvrir les filtres"
                            onClick={(e) => setFilterAnchorEl(filterAnchorEl ? null : e.currentTarget)}
                            disabled={!rawCandidats.length}
                            size="small"
                            sx={{
                                height: 40, width: 40, borderRadius: '10px', border: '1px solid',
                                borderColor: activeFilters ? '#03407b' : '#e2e8f0',
                                background: activeFilters ? '#ebf4fb' : '#fff',
                                color: activeFilters ? '#03407b' : '#64748b',
                                '&:hover': { background: '#f0f7fd', borderColor: '#aed6f1' },
                            }}
                        >
                            <Badge badgeContent={activeFilters} color="primary" sx={{ '& .MuiBadge-badge': { background: '#03407b', fontSize: '0.62rem', height: 16, minWidth: 16 } }}>
                                <Tune sx={{ fontSize: '1.2rem' }} />
                            </Badge>
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
                <AppButton
                    variant="primary" size="small"
                    startIcon={<AutoAwesome fontSize="small" />}
                    onClick={(e) => setLancerAnchorEl(e.currentTarget)}
                    disabled={busy || !jobUuid}
                    loading={loading}
                    sx={{ flex: 1 }}
                >
                    {loading ? 'Recherche en cours…' : rawCandidats.length ? 'Relancer la recherche' : 'Lancer la recherche'}
                </AppButton>
                <Tooltip title="Ré-analyse en profondeur les meilleurs candidats pour affiner le classement">
                    <span style={{ display: 'flex' }}>
                        <AppButton
                            variant="secondary" size="small"
                            startIcon={<Sort fontSize="small" />}
                            onClick={(e) => setRerankAnchorEl(e.currentTarget)}
                            disabled={busy || !jobUuid}
                            loading={rerankLoading}
                        >
                            {rerankLoading ? 'Classement…' : 'Affiner le classement'}
                        </AppButton>
                    </span>
                </Tooltip>
            </Box>

            <SearchFiltersDialog
                open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}
                experienceMin={experienceMin} setExperienceMin={setExperienceMin}
                experienceMax={experienceMax} setExperienceMax={setExperienceMax}
                seniorityLevel={seniorityLevel} setSeniorityLevel={setSeniorityLevel}
                availableSeniorities={availableSeniorities}
                selectedSkills={selectedSkills} setSelectedSkills={setSelectedSkills} availableSkills={availableSkills}
                selectedLanguages={selectedLanguages} setSelectedLanguages={setSelectedLanguages} availableLanguages={availableLanguages}
                selectedEducation={selectedEducation} setSelectedEducation={setSelectedEducation} availableEducation={availableEducation}
                selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation}
                minCandidateExp={minCandidateExp} maxCandidateExp={maxCandidateExp}
            />
            <LancerRecherchePopover
                anchorEl={lancerAnchorEl} onClose={() => setLancerAnchorEl(null)}
                filter={filter} setFilter={setFilter} limit={limit} setLimit={setLimit}
                onLancer={lancerLaRecherche} loading={loading}
            />
            <RerankPopover
                anchorEl={rerankAnchorEl} onClose={() => setRerankAnchorEl(null)}
                filter={filter} setFilter={setFilter}
                shortlistLimit={shortlistLimit} setShortlistLimit={setShortlistLimit}
                limit={limit} setLimit={setLimit}
                onRerank={lancerLeReranking} rerankLoading={rerankLoading}
            />

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: '10px' }}>{error}</Alert>
            )}

            {candidats.length > 0 && !busy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 700 }}>
                        {candidats.length} candidat{candidats.length > 1 ? 's' : ''}
                        {candidats.length !== rawCandidats.length && (
                            <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}> sur {rawCandidats.length}</Box>
                        )}
                    </Typography>
                    <Chip label={`Mode ${filter}`} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: '#03407b', background: '#ebf4fb' }} />
                    <Typography sx={{ ml: 'auto', fontSize: '0.72rem', color: '#94a3b8' }}>Triés par score</Typography>
                </Box>
            )}

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, minHeight: 0 }}>
                {busy ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#03407b', fontSize: '0.82rem', fontWeight: 600, mb: 0.5 }}>
                            <AutoAwesome sx={{ fontSize: 16 }} /> {rerankLoading ? "L'IA affine le classement…" : "L'IA analyse les profils du vivier…"}
                        </Box>
                        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={112} sx={{ borderRadius: '12px' }} />)}
                    </Box>
                ) : candidats.length === 0 ? (
                    rawCandidats.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, color: '#94a3b8', textAlign: 'center' }}>
                            <Tune sx={{ fontSize: 36 }} />
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569' }}>Aucun candidat ne correspond aux filtres</Typography>
                            <Typography sx={{ fontSize: '0.8rem' }}>Élargissez les filtres pour voir plus de résultats.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', py: 2 }}>
                            <Steps current={step} />
                        </Box>
                    )
                ) : (
                    <List disablePadding>
                        {candidats.map((candidat, index) => (
                            <CandidatCard
                                key={candidat.id || index}
                                candidat={candidat} index={index}
                                isSelected={selectedCandidat?.id === candidat.id}
                                onClick={() => setSelectedCandidat(candidat)}
                            />
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
};

export default CandidatsPanel;
